import {Component, OnInit, OnDestroy, Optional} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {CallInvoiceIncident, CallInvoicesDTO, ConfiguratorCallInvoicesService, DatastoreDto} from '@tj/tj-dmp-lib-api';
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, map, startWith, switchMap, takeUntil, finalize} from 'rxjs/operators';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {invoiceListRow, InvoiceListRow, InvoiceListFilter, InvoiceRecipientFilter, InvoiceTransactionFilter, invoiceSearchText} from './invoice-list-view';

@Component({
    templateUrl: './invoice.component.html',
    styleUrls: ['./invoice.component.scss'],
    standalone: false
})
export class InvoiceComponent implements OnInit, OnDestroy {
    get locale(): string { return this.translate?.getCurrentLang() === 'en' ? 'en-GB' : 'fr-FR'; }

    private readonly loads$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();
    incidents: CallInvoiceIncident[] = [];
    incidentsLoadError = false;
    loading = false;
    loadError = false;
    deletingId: string | null = null;
    breadcrumb: MenuItem[] = [];
    datastore: DatastoreDto;
    callInvoices: CallInvoicesDTO[] = [];
    rows: InvoiceListRow[] = [];
    filteredRows: InvoiceListRow[] = [];
    search = '';
    activeFilter: InvoiceListFilter = 'all';
    recipientFilter: InvoiceRecipientFilter = 'all';
    transactionFilter: InvoiceTransactionFilter = 'all';
    first = 0;
    readonly recipientFilters: {value: InvoiceRecipientFilter; label: string}[] = [
        {value: 'all', label: 'invoiceList.allRecipients'},
        {value: 'TENANT_TO_PROVIDER', label: 'invoiceList.providerRecipients'},
        {value: 'TENANT_TO_PARTNER', label: 'invoiceList.partnerRecipients'}
    ];
    readonly transactionFilters: {value: InvoiceTransactionFilter; label: string}[] = [
        {value: 'all', label: 'invoiceList.allTransactions'},
        {value: 'command', label: 'invoiceList.purchases'},
        {value: 'lead', label: 'invoiceList.leads'}
    ];
    readonly filters: {value: InvoiceListFilter; label: string}[] = [
        {value: 'all', label: 'invoiceList.allFilter'}, {value: 'incidents', label: 'invoiceList.incidentFilter'},
        {value: 'validation', label: 'invoiceList.validationFilter'}, {value: 'email', label: 'invoiceList.emailFilter'},
        {value: 'automatic', label: 'invoiceList.automaticFilter'},
        {value: 'manual', label: 'invoiceList.manualFilter'}, {value: 'incomplete', label: 'invoiceList.incompleteFilter'}
    ];
    state: {message: string};

    constructor(
        private confirmationService: ConfirmationService,
        private router: Router,
        private messageService: MessageService,
        private configuratorCallInvoicesService: ConfiguratorCallInvoicesService,
        @Optional() private translate: TranslateService = null) {
        this.state = this.router.getCurrentNavigation()?.extras?.state as {message: string} | undefined;
    }

    private t(key: string, params?: object): string { return this.translate?.instant('invoiceList.' + key, params) || 'invoiceList.' + key; }

    private rebuildRows(): void {
        this.breadcrumb = [{label: this.t('applications')}, {label: this.t('title')}];
        const partnerNames = new Map((this.datastore?.partners || []).map(partner => [partner.code, partner.name]));
        this.rows = this.callInvoices.filter(Boolean).map(call => invoiceListRow(call, this.incidents, partnerNames, (key, params) => this.t(key, params)));
        this.filterRows();
    }

    ngOnInit(): void {
        this.rebuildRows();
        this.translate?.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.rebuildRows());
        this.loads$.pipe(startWith(null), switchMap(() => {
            this.callInvoices = []; this.incidents = []; this.rows = []; this.filteredRows = [];
            this.loadError = false; this.incidentsLoadError = false;
            const tenant = this.datastore?.code;
            this.loading = !!tenant;
            if (!tenant) { return of({calls: [], incidents: []}); }
            return forkJoin({
                calls: this.configuratorCallInvoicesService.getAllCallInvoicessUsingGET(tenant, 'response').pipe(map(response => response.body || [])),
                incidents: this.configuratorCallInvoicesService.getIncidents(tenant).pipe(catchError(() => { this.incidentsLoadError = true; return of([]); }))
            }).pipe(catchError(() => { this.loadError = true; return of({calls: [], incidents: []}); }));
        }), takeUntil(this.destroy$)).subscribe(result => {
            this.callInvoices = result.calls; this.incidents = result.incidents; this.loading = false;
            if (this.incidentsLoadError && this.activeFilter === 'incidents') { this.activeFilter = 'all'; }
            this.rebuildRows();
        });
        if (this.state?.message === 'success_created' || this.state?.message === 'success_updated') {
            this.messageService.add({severity: 'success', summary: this.t('saved'), detail: this.t(this.state.message === 'success_created' ? 'created' : 'updated')});
        }
    }

    datastoreChange(datastore: DatastoreDto): void {
        if (this.datastore?.code !== datastore?.code) { this.resetFilters(); }
        this.datastore = datastore;
    }

    loadAll(): void { this.loads$.next(); }
    get automaticCount(): number { return this.rows.filter(row => row.automatic).length; }
    get pendingValidationCount(): number { return this.rows.reduce((total, row) => total + row.pendingValidation, 0); }
    get pendingEmailCount(): number { return this.rows.reduce((total, row) => total + row.pendingEmailCount, 0); }
    get openIncidentCount(): number { return this.rows.reduce((total, row) => total + row.incidents, 0); }

    incidentCount(callId: string): number {
        return this.incidents.filter(incident => incident.callInvoiceId === callId && incident.status !== 'RESOLVED').length;
    }

    selectFilter(value: InvoiceListFilter): void { this.activeFilter = value; this.filterRows(); }
    filterRows(): void {
        const terms = invoiceSearchText(this.search.trim()).split(/\s+/).filter(Boolean);
        this.filteredRows = this.rows.filter(row => (this.recipientFilter === 'all' || row.billingDirection === this.recipientFilter)
            && (this.transactionFilter === 'all' || row.transactionType === this.transactionFilter)
            && terms.every(term => row.searchText.includes(term)) && (
            this.activeFilter === 'all' || this.activeFilter === 'incidents' && row.incidents > 0
            || this.activeFilter === 'validation' && row.pendingValidation > 0
            || this.activeFilter === 'email' && row.pendingEmailCount > 0
            || this.activeFilter === 'automatic' && row.automatic || this.activeFilter === 'manual' && !row.automatic
            || this.activeFilter === 'incomplete' && row.missingFields.length > 0));
        this.first = 0;
    }
    resetFilters(): void { this.search = ''; this.recipientFilter = 'all'; this.transactionFilter = 'all'; this.selectFilter('all'); }

    confirmDelete(row: InvoiceListRow): void {
        const tenant = this.datastore?.code;
        if (!tenant || !row.id || this.deletingId) { return; }
        this.confirmationService.confirm({
            header: this.t('deleteTitle'), message: this.t('deleteConfirm', {name: row.name}),
            acceptLabel: this.t('delete'), rejectLabel: this.t('cancel'), acceptIcon: 'fas fa-trash',
            accept: () => {
                this.deletingId = row.id;
                this.configuratorCallInvoicesService.deleteCallInvoicesUsingDELETE(tenant, row.id).pipe(
                    finalize(() => this.deletingId = null), takeUntil(this.destroy$)
                ).subscribe({next: () => {
                    if (this.datastore?.code === tenant) { this.loadAll(); }
                    this.messageService.add({severity: 'success', summary: this.t('deleted'), detail: this.t('deletedDetail', {name: row.name})});
                }, error: () => this.messageService.add({severity: 'error', summary: this.t('deleteFailed'), detail: this.t('deleteFailedDetail')})});
            }
        });
    }
    ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); this.loads$.complete(); }
}
